import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';

import { Box } from '@mui/material';
import FormGenerator from '@sharedCR/common/formGenerator';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';

import { getBorrowersInfoAction } from 'store/loanApplications/actions';

import { NEW_ENTITY } from '../../../../../EditEntityModal.constants';
import { pagesPropTypes } from '../../../MainContent.constants';
import { sectionStyles } from '../Declarations.styles';

import CreditReportEventDates from './CreditReportEventDates';
import { getConfig, getCreditEventDatesConfig } from './Finances.config';
import {
  getRefreshedCreditEventsEntityData,
  getVisibleCreditEventDateKeys,
  normalizeForm,
} from './Finances.utils';

const Finances = ({
  changedFields,
  isLoading,
  isSaving,
  form,
  validationErrors,
  validateField,
  handleChange,
  entityId,
  setEntityConfig,
  setForm,
  setInForm,
  disabledPendingChangesHighlight,
  pendingChanges,
  applicationData,
  updateEntitiesApplicationData,
}) => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();

  const finances = form[entityId]?.declarations?.finances;
  const isLastMonthsBankruptcy = finances?.isLastMonthsBankruptcy;
  const bankruptcyFilingDate = finances?.bankruptcyFilingDate;
  const entityData = applicationData?.[entityId];
  const storedFinances = entityData?.declarations?.finances;
  const isNewEntity = entityId === NEW_ENTITY;

  // Joined so the config is rebuilt only when the set of shown dates changes, not on every keystroke.
  const visibleDateKeysId = useMemo(
    () =>
      isNewEntity ? '' : getVisibleCreditEventDateKeys({ finances, storedFinances }).join(','),
    [finances, isNewEntity, storedFinances],
  );

  const config = useMemo(
    () =>
      getConfig({
        entityId,
        isLastMonthsBankruptcy,
      }),
    [entityId, isLastMonthsBankruptcy],
  );

  const creditEventDatesConfig = useMemo(
    () =>
      getCreditEventDatesConfig({
        entityId,
        visibleDateKeys: visibleDateKeysId ? visibleDateKeysId.split(',') : [],
        bankruptcyFilingDate,
      }),
    [entityId, visibleDateKeysId, bankruptcyFilingDate],
  );

  const onCreditEventsRefreshed = useCallback(
    () =>
      dispatch(getBorrowersInfoAction({ applicationId, params: { includes: 'residences' } })).then(
        borrowers =>
          updateEntitiesApplicationData?.(
            getRefreshedCreditEventsEntityData({ borrowers, entityId, entityData }),
          ),
      ),
    [applicationId, dispatch, entityData, entityId, updateEntitiesApplicationData],
  );

  useEffect(() => {
    setForm(form => normalizeForm({ form, entityId }));
  }, [config, creditEventDatesConfig, entityId, setForm]);

  useEffect(() => {
    if (!config) {
      return;
    }

    setEntityConfig({ groups: [...config.groups, ...creditEventDatesConfig.groups] });
  }, [config, creditEventDatesConfig, setEntityConfig]);

  return (
    <>
      <FormGenerator
        isNestedForm
        changedFields={changedFields}
        config={config}
        disabledPendingChangesHighlight={disabledPendingChangesHighlight}
        form={form}
        formName={FormNames.DECLARATIONS}
        handleChange={handleChange}
        isLoading={isSaving || isLoading}
        numberOfColumns={1}
        pendingChanges={pendingChanges}
        setInForm={setInForm}
        validateField={validateField}
        validationErrors={validationErrors}
      />
      {!isNewEntity && (
        <Box sx={sectionStyles}>
          {visibleDateKeysId && (
            <FormGenerator
              ignoreEmptyGroups
              isNestedForm
              changedFields={changedFields}
              config={creditEventDatesConfig}
              disabledPendingChangesHighlight={disabledPendingChangesHighlight}
              form={form}
              formName={FormNames.DECLARATIONS}
              handleChange={handleChange}
              isLoading={isSaving || isLoading}
              numberOfColumns={3}
              pendingChanges={pendingChanges}
              setInForm={setInForm}
              validateField={validateField}
              validationErrors={validationErrors}
            />
          )}
          <CreditReportEventDates
            applicationId={applicationId}
            borrowerId={entityId}
            creditReportReferenceNumber={entityData?.lastCreditReportReferenceNumber ?? null}
            finances={storedFinances ?? null}
            isDisabled={isSaving || isLoading}
            onRefreshed={onCreditEventsRefreshed}
          />
        </Box>
      )}
    </>
  );
};

Finances.propTypes = pagesPropTypes;

export default memo(Finances);
